import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useSeasonService } from "~pages/Seasons/hooks/useSeasonService.ts";
import { defaultSeasonsExportFieldNames, seasonsExportFields } from "~pages/Seasons/SeasonListPage/constants/exportFields.ts";
import { useExtractSeasonFromScopeByCompetitionId } from "~pages/Seasons/SeasonListPage/hooks/useExtractSeasonFromScopeByCompetitionId.ts";
import { useFilteredSeasons } from "~pages/Seasons/SeasonListPage/hooks/useFilteredSeasons.ts";
import { useGetSeasonsTableColumns } from "~pages/Seasons/SeasonListPage/hooks/useGetSeasonsTableColumns.tsx";
import { useGetSeasonsTableFilters } from "~pages/Seasons/SeasonListPage/hooks/useGetSeasonsTableFilters.tsx";

const SeasonListPage = () => {
  const { sport, customerId, organizationId, competitionId } = useParams();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasons.listPage;
  const paginationConfig = useHandlePagination(50, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const location = useLocation();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { importSeasons, exportSeasons, isExportPending, deleteSeason, isDeletePending } = useSeasonService();
  const dataTableImportConfiguration = uiConfiguration.pages.seasons.listPage.importConfig;

  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/${competitionId}/seasons/add`);

  const handleDelete = useCallback(
    async (seasonId: string) => {
      return await deleteSeason(seasonId);
    },
    [deleteSeason],
  );

  const filtersConfig = useGetSeasonsTableFilters();
  const columns = useGetSeasonsTableColumns();

  const { seasons, isLoading, refetch, competitionName } = useExtractSeasonFromScopeByCompetitionId({ competitionId });

  const clientSeasons = useFilteredSeasons({
    seasons,
    currentFilter: filtersConfig.currentFilter,
    searchText,
  });

  return (
    <Page title={competitionName}>
      <DataTable
        handleRefresh={async () => {
          await refetch();
        }}
        title={formatMessage({ id: "seasonsListPage.title" })}
        columns={columns}
        rowActions={{
          base: {
            delete: {
              onClick: (row) => handleDelete(row.seasonId),
              loading: isDeletePending,
            },
            manage: {
              onClick: (row) => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${row.seasonId}/details`),
            },
          },
        }}
        rowKey={"seasonId"}
        rowNameKey={"nameLocal"}
        dataSource={clientSeasons}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasons`}
        isDataLoading={isLoading}
        handleAdd={handleAdd}
        addButtonIntlKey={"season"}
        filtersConfig={{
          ...filtersConfig,
          filtersLocalStorageKey: `${sport}.tables.settings.filters.seasons`,
        }}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasons`,
          defaultSorter: {
            columnKey: "year",
            order: "descend",
          },
        }}
        exportConfig={{
          handleExport: exportSeasons,
          loading: isExportPending,
          defaultFields: defaultSeasonsExportFieldNames,
          groupedFields: seasonsExportFields,
          localStorageKey: "export.seasons",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: importSeasons,
        }}
        permissionNames={[PermissionNames.SEASON]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, total: clientSeasons.length }}
      />
    </Page>
  );
};

export default SeasonListPage;
